/**
 * LiveTradeFeed — live open positions + recent closed history for the 3 Core bots.
 *
 * Layout:
 *  • Bot tabs (Sigmai · Sugair · Jairvis)
 *  • Sub-tabs OPEN / CLOSED
 *  • Table rows with symbol, direction, volume, entry/exit, P/L, duration
 *  • Live refresh every 25s for OPEN, every 90s for CLOSED
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Radio, TrendingUp, TrendingDown, Clock, ArrowRight } from 'lucide-react';
import { apiFetch } from '../utils/apiFetch';
import { BOT_CONFIG } from './MTModule';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtUsd = (n) => {
  if (n === null || n === undefined || !isFinite(n)) return '—';
  const s = n >= 0 ? '+' : '-';
  const v = Math.abs(n);
  if (v >= 1000) return `${s}$${(v / 1000).toFixed(2)}K`;
  return `${s}$${v.toFixed(2)}`;
};
const fmtVol = (v) => (v === null || v === undefined) ? '—' : Number(v).toFixed(2);
const fmtPrice = (p) => (p === null || p === undefined) ? '—' : Number(p).toFixed(5).replace(/0+$/, '').replace(/\.$/, '');
const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};
const fmtDuration = (ms) => {
  if (!ms || ms <= 0) return '—';
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h${m % 60 ? ` ${m % 60}m` : ''}`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
};

const DirBadge = ({ type }) => {
  const isBuy = type === 'buy';
  const color = isBuy ? '#22c55e' : '#ef4444';
  const Icon = isBuy ? TrendingUp : TrendingDown;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold tracking-widest uppercase"
      style={{ background: `${color}18`, color, border: `1px solid ${color}55` }}
    >
      <Icon className="w-2.5 h-2.5" /> {type || '—'}
    </span>
  );
};

const EmptyRow = ({ children }) => (
  <div className="text-center py-6 text-[11px] text-[#556475] italic">{children}</div>
);

const LiveTradeFeed = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBot, setSelectedBot] = useState(null);
  const [subTab, setSubTab] = useState('open'); // 'open' | 'closed'
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  const load = () => {
    apiFetch(`${API}/ai-bots/live-feed`)
      .then((r) => r.json())
      .then((d) => {
        setData(d);
        setLoading(false);
        setLastRefresh(Date.now());
        if (!selectedBot && d?.bots?.[0]) setSelectedBot(d.bots[0].bot);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // Auto-refresh every 25s
    const t = setInterval(load, 25000);
    return () => clearInterval(t);
  }, []);

  const bots = data?.bots || [];
  const current = useMemo(() => bots.find((b) => b.bot === selectedBot) || bots[0], [bots, selectedBot]);

  if (loading) {
    return (
      <Card className="border" style={{ background: 'rgba(4,8,22,0.6)', borderColor: 'rgba(0,244,255,0.18)' }}>
        <CardContent className="p-4 text-center text-[11px] text-[#9EB4C0]">Loading live feed…</CardContent>
      </Card>
    );
  }

  if (bots.length === 0) {
    return (
      <Card className="border" style={{ background: 'rgba(4,8,22,0.6)', borderColor: 'rgba(0,244,255,0.18)' }} data-testid="live-trade-feed">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <Radio className="w-4 h-4 text-[#00f4ff]" />
            <h3 className="text-base font-bold text-white">Live Trade Feed</h3>
          </div>
          <p className="text-[11px] text-[#9EB4C0]">Awaiting live positions from the 3 Core AI accounts.</p>
        </CardContent>
      </Card>
    );
  }

  const rows = current ? (subTab === 'open' ? current.open : current.closed) || [] : [];
  const openTotalPL = (current?.open || []).reduce((s, p) => s + (p.profit || 0), 0);

  return (
    <Card
      className="overflow-hidden border"
      style={{
        background: 'linear-gradient(180deg, rgba(0,244,255,0.05), rgba(4,8,22,0.85))',
        borderColor: 'rgba(0,244,255,0.30)',
        boxShadow: '0 18px 60px -25px rgba(0,244,255,0.35)',
      }}
      data-testid="live-trade-feed"
    >
      {/* Header */}
      <div className="px-5 py-2 flex items-center justify-between"
        style={{ background: 'rgba(0,244,255,0.06)', borderBottom: '1px solid rgba(0,244,255,0.18)' }}>
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22c55e] opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#22c55e]" />
          </span>
          <Radio className="w-4 h-4 text-[#00f4ff]" />
          <h3 className="text-sm font-bold tracking-[0.15em] uppercase text-white">Live Trade Feed</h3>
          <span className="text-[10px] tracking-[0.2em] uppercase text-[#00f4ff] hidden sm:inline">Open + last 30d</span>
        </div>
        <div className="text-[9px] text-[#556475] tabular-nums">
          Updated {new Date(lastRefresh).toLocaleTimeString()}
        </div>
      </div>

      <CardContent className="p-4">
        {/* Bot selector pills */}
        <div className="flex gap-2 flex-wrap mb-3">
          {bots.map((b) => {
            const cfg = BOT_CONFIG[b.bot] || {};
            const active = b.bot === (current?.bot);
            return (
              <button
                key={b.bot}
                onClick={() => setSelectedBot(b.bot)}
                className="px-3 py-1.5 rounded-full text-[10px] font-bold tracking-[0.2em] uppercase transition-all flex items-center gap-1.5"
                style={{
                  background: active ? cfg.color : 'transparent',
                  color: active ? '#020817' : cfg.color || '#00f4ff',
                  border: `1px solid ${cfg.color || '#00f4ff'}66`,
                  boxShadow: active ? `0 0 20px -4px ${cfg.glow}` : 'none',
                }}
                data-testid={`feed-tab-${b.bot}`}
              >
                {cfg.label || b.bot}
                <span className="text-[9px] opacity-75">
                  {b.open_count}op · {b.closed_count_30d}cl
                </span>
              </button>
            );
          })}
        </div>

        {/* Sub-tabs OPEN / CLOSED */}
        <div className="flex gap-1 mb-3 border-b border-[#00f4ff]/15">
          {['open', 'closed'].map((tab) => {
            const active = subTab === tab;
            const count = tab === 'open' ? (current?.open?.length || 0) : (current?.closed?.length || 0);
            return (
              <button
                key={tab}
                onClick={() => setSubTab(tab)}
                className="px-4 py-2 text-[10px] font-bold tracking-[0.25em] uppercase transition-colors relative"
                style={{
                  color: active ? '#00f4ff' : '#9EB4C0',
                  borderBottom: active ? '2px solid #00f4ff' : '2px solid transparent',
                  marginBottom: '-1px',
                }}
                data-testid={`feed-subtab-${tab}`}
              >
                {tab === 'open' ? 'Open positions' : 'Recent closed'} · {count}
              </button>
            );
          })}
          {subTab === 'open' && rows.length > 0 && (
            <div className="ml-auto flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase">
              <span className="text-[#9EB4C0]">Floating P/L</span>
              <span className={openTotalPL >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]'}
                style={{ textShadow: `0 0 10px ${openTotalPL >= 0 ? 'rgba(34,197,94,0.6)' : 'rgba(239,68,68,0.6)'}` }}>
                {fmtUsd(openTotalPL)}
              </span>
            </div>
          )}
        </div>

        {/* Table */}
        {rows.length === 0 ? (
          <EmptyRow>
            {subTab === 'open'
              ? 'No open positions right now — the bot is waiting for setup.'
              : 'No closed trades in the last 30 days for this window.'}
          </EmptyRow>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]" data-testid="feed-table">
              <thead>
                <tr className="text-[8px] tracking-[0.2em] uppercase text-[#556475]">
                  <th className="text-left py-1.5 pl-2">Symbol</th>
                  <th className="text-left py-1.5">Dir</th>
                  <th className="text-right py-1.5">Vol</th>
                  {subTab === 'open' ? (
                    <>
                      <th className="text-right py-1.5">Open @</th>
                      <th className="text-right py-1.5">Now</th>
                      <th className="text-right py-1.5">P/L</th>
                      <th className="text-right py-1.5 pr-2">Since</th>
                    </>
                  ) : (
                    <>
                      <th className="text-right py-1.5">Entry → Exit</th>
                      <th className="text-right py-1.5">P/L</th>
                      <th className="text-right py-1.5">Duration</th>
                      <th className="text-right py-1.5 pr-2">Closed</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className="border-t border-white/5 hover:bg-white/5 transition">
                    <td className="py-2 pl-2 font-mono text-white">{row.symbol || '—'}</td>
                    <td className="py-2"><DirBadge type={row.type} /></td>
                    <td className="py-2 text-right font-mono text-[#cfd9e3]">{fmtVol(row.volume)}</td>
                    {subTab === 'open' ? (
                      <>
                        <td className="py-2 text-right font-mono text-[#9EB4C0]">{fmtPrice(row.open_price)}</td>
                        <td className="py-2 text-right font-mono text-white">{fmtPrice(row.current_price)}</td>
                        <td className="py-2 text-right font-mono font-bold" style={{ color: row.profit >= 0 ? '#22c55e' : '#ef4444' }}>
                          {fmtUsd(row.profit)}
                        </td>
                        <td className="py-2 pr-2 text-right text-[#9EB4C0] flex items-center gap-1 justify-end">
                          <Clock className="w-2.5 h-2.5" /> {fmtTime(row.open_time)}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-2 text-right font-mono text-[#9EB4C0]">
                          <span className="text-[#9EB4C0]">{fmtPrice(row.open_price)}</span>
                          <ArrowRight className="inline w-2.5 h-2.5 mx-1 text-[#556475]" />
                          <span className="text-white">{fmtPrice(row.close_price)}</span>
                        </td>
                        <td className="py-2 text-right font-mono font-bold" style={{ color: row.profit >= 0 ? '#22c55e' : '#ef4444' }}>
                          {fmtUsd(row.profit)}
                        </td>
                        <td className="py-2 text-right text-[#cfd9e3] font-mono">{fmtDuration(row.duration_ms)}</td>
                        <td className="py-2 pr-2 text-right text-[#9EB4C0]">{fmtTime(row.close_time)}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="mt-3 text-[9px] text-[#556475] italic text-center tracking-wider">
          Live positions refresh every 25s · closed history covers last 30 days · powered by MetaApi.cloud
        </p>
      </CardContent>
    </Card>
  );
};

export default LiveTradeFeed;
